import React, { useState, useEffect } from 'react';
import componentStyles from './parlayGenerator.module.css';
import globalStyles from '../globals.module.css';
import SmallLoadingAlt from '../global/smallLoadingAlt';
import { generateParlays as generateParlaysAPI, getParlayOptions } from '../../service/parlayService';
import SessionService from '../../service/sessionService';
import ParlaySettingsModal from './parlaySettingsModal';
import ParlayView from './parlayView';
import { FaCog, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import PageView from '../common/pageView';
import AccessLockedPanel from '../common/accessLockedPanel';
import RegistrationModal from '../betFeed/registrationModal';
import eventBus from '../../service/eventBus';
import { EVENT_NOTIFICATION_PUSH, NOTIFICATIONS_ERROR } from '../../utils/constants';
import NotificationModel from '../../model/notificationModel';
import AnalyticsService from '../../service/analyticsService';
import { EVENT_PARLAY_SETTINGS_OPENED, EVENT_PARLAY_GENERATED } from '../../utils/analyticsConstants';
import AccessService from '../../service/accessService';

const HIDDEN_PARLAY_SPORTS = new Set(['cfb', 'mma', 'pga']);
const isVisibleParlaySport = (sport) => !HIDDEN_PARLAY_SPORTS.has(String(sport || '').toLowerCase());

/**
 * ParlayGenerator Component
 * 
 * This component generates parlay betting combinations from available betting recommendations.
 * It allows users to configure:
 * - Number of events per parlay (2-20)
 * - Maximum number of parlays to generate (1-10, default: 2)
 * - Sport filtering (optional - leave empty for all sports)
 * - Bet types filtering (optional - leave all unchecked for all bet types)
 * - Prop market types filtering (over/under) - only shown when Prop bet type is selected
 * - View mode (simplified or normal)
 * 
 * The component displays generated parlays with their individual bet legs.
 * Supports two view modes: simplified ticket view (default) and detailed normal view.
 */
const ParlayGenerator = () => {
    const componentId = 'ParlayGenerator';
    
    // Get session service instance
    const sessionService = SessionService.getInstance();
    const accessService = AccessService.getInstance();
    const access = accessService.getAccess();
    const canUseParlayGenerator = accessService.isProOrAdmin();
    
    // Configuration state - load from local settings or use defaults
    const [numEvents, setNumEvents] = useState(() => {
        return sessionService.getLocalSetting('parlayGenerator_numEvents', 5);
    });
    const [maxParlays, setMaxParlays] = useState(() => {
        return sessionService.getLocalSetting('parlayGenerator_maxParlays', 2);
    });
    // Note: Sports filter is already multi-select via button toggles
    const [selectedSports, setSelectedSports] = useState(() => {
        const saved = sessionService.getLocalSetting('parlayGenerator_selectedSports', []);
        return Array.isArray(saved) ? saved.filter(isVisibleParlaySport) : [];
    }); // Multi-select array
    const [userSports, setUserSports] = useState([]); // User's active sports from session
    const [selectedBetTypes, setSelectedBetTypes] = useState(() => {
        const saved = sessionService.getLocalSetting('parlayGenerator_selectedBetTypes', []);
        return Array.isArray(saved) ? saved : [];
    }); // Multi-select array for bet types
    const [propMarketTypes, setPropMarketTypes] = useState(() => {
        const saved = sessionService.getLocalSetting('parlayGenerator_propMarketTypes', []);
        return Array.isArray(saved) ? saved : [];
    }); // Multi-select array for scouting report prop market types
    const [propDirections, setPropDirections] = useState(() => {
        const saved = sessionService.getLocalSetting('parlayGenerator_propDirections', []);
        return Array.isArray(saved) ? saved : [];
    }); // Multi-select array for scouting report prop directions
    const [viewMode] = useState(() => {
        return sessionService.getLocalSetting('parlayGenerator_viewMode', 'simplified');
    });
    
    // Scouting reports are sunset for now; keep their implementation available
    // while forcing the visible generator onto the Sniped Odds source.
    const [source, setSource] = useState('statAnalysis');
    
    // Optional Sniped Odds direction. Price is no longer exposed.
    const [snipedType, setSnipedType] = useState(() => {
        const saved = sessionService.getLocalSetting('parlayGenerator_snipedType', null);
        return ['overs', 'unders'].includes(saved) ? saved : null;
    });
    
    // Sniped odds filters - optional filters for stat analysis source
    const [snipedFilters, setSnipedFilters] = useState(() => {
        const saved = sessionService.getLocalSetting('parlayGenerator_snipedFilters', {});
        return {
            betType: 'player_prop',
            propTypes: Array.isArray(saved.propTypes) ? saved.propTypes : [],
            isDfs: saved.isDfs !== undefined ? saved.isDfs : null,
            recommendation: saved.recommendation || null,
            minValueScore: saved.minValueScore !== undefined ? saved.minValueScore : null,
            limit: saved.limit || 100,
            includeAllOpportunities: saved.includeAllOpportunities !== undefined ? saved.includeAllOpportunities : false
        };
    });
    
    // Source-specific options from /parlay/options
    const [availableBetTypes, setAvailableBetTypes] = useState([]);
    const [availableScoutingPropMarketTypes, setAvailableScoutingPropMarketTypes] = useState([]);
    const [availablePropDirections, setAvailablePropDirections] = useState([]);
    const [availableSnipedTypes, setAvailableSnipedTypes] = useState([]);
    const [availableSnipedPropTypes, setAvailableSnipedPropTypes] = useState([]);
    const [parlayOptionsLoading, setParlayOptionsLoading] = useState(false);
    const [parlayOptionsError, setParlayOptionsError] = useState('');
    
    // Collapsible sections state - tracks which sections are expanded
    const [expandedSections, setExpandedSections] = useState(() => {
        const saved = sessionService.getLocalSetting('parlayGenerator_expandedSections', {
            dataSource: true,
            numEvents: true,
            maxParlays: true,
            sportFilter: true,
            betTypes: true,
            propMarketTypes: true,
            snipedType: true,
            snipedFilters: false, // Collapsed by default (progressive disclosure)
            propDirections: true,
            viewMode: true
        });
        // Ensure all sections have a default value
        return {
            dataSource: saved.dataSource !== undefined ? saved.dataSource : true,
            numEvents: saved.numEvents !== undefined ? saved.numEvents : true,
            maxParlays: saved.maxParlays !== undefined ? saved.maxParlays : true,
            sportFilter: saved.sportFilter !== undefined ? saved.sportFilter : true,
            betTypes: saved.betTypes !== undefined ? saved.betTypes : true,
            propMarketTypes: saved.propMarketTypes !== undefined ? saved.propMarketTypes : true,
            snipedType: saved.snipedType !== undefined ? saved.snipedType : true,
            snipedFilters: saved.snipedFilters !== undefined ? saved.snipedFilters : false,
            propDirections: saved.propDirections !== undefined ? saved.propDirections : true,
            viewMode: saved.viewMode !== undefined ? saved.viewMode : true
        };
    });
    
    // Data state
    const [parlays, setParlays] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // UI state
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(true);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    useEffect(() => {
        if (!canUseParlayGenerator && access.tier !== 'anonymous') {
            setShowUpgradeModal(true);
        }
    }, [canUseParlayGenerator, access.tier]);
    
    
    /**
     * Get user's filtered active sports from session service
     * Uses intersection of active sports (from API) and user's enabled sports
     */
    useEffect(() => {
        if (!sessionService.isAuthenticated()) {
            setUserSports([]);
        } else {
            // Get filtered active sports (intersection of active sports and user's enabled sports)
            setUserSports(sessionService.getFilteredActiveSports().filter(isVisibleParlaySport));
        }
    }, [sessionService]);
    
    /**
     * Save numEvents to local settings when it changes
     */
    useEffect(() => {
        sessionService.setLocalSetting('parlayGenerator_numEvents', numEvents);
    }, [numEvents, sessionService]);
    
    /**
     * Save maxParlays to local settings when it changes
     */
    useEffect(() => {
        sessionService.setLocalSetting('parlayGenerator_maxParlays', maxParlays);
    }, [maxParlays, sessionService]);
    
    /**
     * Save viewMode to local settings when it changes
     */
    useEffect(() => {
        sessionService.setLocalSetting('parlayGenerator_viewMode', viewMode);
    }, [viewMode, sessionService]);
    
    /**
     * Save selectedSports to local settings when it changes
     */
    useEffect(() => {
        sessionService.setLocalSetting('parlayGenerator_selectedSports', selectedSports);
    }, [selectedSports, sessionService]);
    
    /**
     * Save selectedBetTypes to local settings when it changes
     */
    useEffect(() => {
        sessionService.setLocalSetting('parlayGenerator_selectedBetTypes', selectedBetTypes);
    }, [selectedBetTypes, sessionService]);
    
    /**
     * Save propMarketTypes to local settings when it changes
     */
    useEffect(() => {
        sessionService.setLocalSetting('parlayGenerator_propMarketTypes', propMarketTypes);
    }, [propMarketTypes, sessionService]);

    /**
     * Save propDirections to local settings when it changes
     */
    useEffect(() => {
        sessionService.setLocalSetting('parlayGenerator_propDirections', propDirections);
    }, [propDirections, sessionService]);
    
    /**
     * Save expandedSections to local settings when it changes
     */
    useEffect(() => {
        sessionService.setLocalSetting('parlayGenerator_expandedSections', expandedSections);
    }, [expandedSections, sessionService]);
    
    /**
     * Save source to local settings when it changes
     */
    useEffect(() => {
        sessionService.setLocalSetting('parlayGenerator_source', source);
    }, [source, sessionService]);
    
    /**
     * Save snipedType to local settings when it changes
     */
    useEffect(() => {
        sessionService.setLocalSetting('parlayGenerator_snipedType', snipedType);
    }, [snipedType, sessionService]);
    
    /**
     * Save snipedFilters to local settings when it changes
     */
    useEffect(() => {
        sessionService.setLocalSetting('parlayGenerator_snipedFilters', snipedFilters);
    }, [snipedFilters, sessionService]);
    
    /**
     * Clear filters when switching sources (per user decision)
     */
    useEffect(() => {
        if (source === 'scoutingReports') {
            // Clear sniped-specific filters when switching to scouting reports
            setSnipedType(null);
            setSnipedFilters({
                betType: 'player_prop',
                propTypes: [],
                isDfs: null,
                recommendation: null,
                minValueScore: null,
                limit: 100,
                includeAllOpportunities: false
            });
        } else if (source === 'statAnalysis') {
            // Clear scouting report filters when switching to stat analysis
            setSelectedBetTypes([]);
            setPropMarketTypes([]);
            setPropDirections([]);
        }
    }, [source]); // Only run when source changes
    
    /**
     * Fetch parlay options when source or selected sports change.
     */
    useEffect(() => {
        let isCancelled = false;

        const fetchOptions = async () => {
            if (!canUseParlayGenerator) {
                setAvailableBetTypes([]);
                setAvailableScoutingPropMarketTypes([]);
                setAvailablePropDirections([]);
                setAvailableSnipedTypes([]);
                setAvailableSnipedPropTypes([]);
                setParlayOptionsError('');
                return;
            }

            const sportsForOptions = selectedSports.length > 0 ? selectedSports : userSports;
            if (sportsForOptions.length === 0) {
                setAvailableBetTypes([]);
                setAvailableScoutingPropMarketTypes([]);
                setAvailablePropDirections([]);
                setAvailableSnipedTypes([]);
                setAvailableSnipedPropTypes([]);
                setParlayOptionsError('');
                return;
            }

            setParlayOptionsLoading(true);
            setParlayOptionsError('');

            try {
                const { options } = await getParlayOptions({
                    sports: sportsForOptions,
                    source
                });

                if (isCancelled) {
                    return;
                }

                if (source === 'scoutingReports') {
                    setAvailableBetTypes(options.betTypes || []);
                    setAvailableScoutingPropMarketTypes(options.propMarketTypes || []);
                    setAvailablePropDirections(options.propDirections || []);
                    setAvailableSnipedTypes([]);
                    setAvailableSnipedPropTypes([]);
                } else {
                    const normalizedSelectedSports = sportsForOptions.map((sport) => String(sport || '').toUpperCase());
                    const mergedPropTypes = normalizedSelectedSports.length <= 1
                        ? (options.propTypes || [])
                        : normalizedSelectedSports.flatMap((sport) => options.propTypesBySport?.[sport] || []);
                    const dedupedPropTypes = Array.from(
                        new Map(mergedPropTypes.map((item) => [item.key, item])).values()
                    );

                    setAvailableSnipedTypes((options.snipedTypes || []).filter((option) => option.key !== 'price'));
                    setAvailableSnipedPropTypes(dedupedPropTypes);
                    setAvailableBetTypes([]);
                    setAvailableScoutingPropMarketTypes([]);
                    setAvailablePropDirections([]);
                }
            } catch (error) {
                if (isCancelled) {
                    return;
                }
                console.error('Error fetching parlay options:', error);
                setParlayOptionsError('Unable to load parlay options right now.');
                setAvailableBetTypes([]);
                setAvailableScoutingPropMarketTypes([]);
                setAvailablePropDirections([]);
                setAvailableSnipedTypes([]);
                setAvailableSnipedPropTypes([]);
            } finally {
                if (!isCancelled) {
                    setParlayOptionsLoading(false);
                }
            }
        };

        fetchOptions();

        return () => {
            isCancelled = true;
        };
    }, [source, selectedSports, userSports, canUseParlayGenerator]);

    /**
     * Prune scouting-report selections when options change.
     */
    useEffect(() => {
        if (source !== 'scoutingReports') {
            return;
        }

        const availableBetTypeKeys = new Set(availableBetTypes.map((item) => item.key));
        const availableMarketKeys = new Set(availableScoutingPropMarketTypes.map((item) => item.key));
        const availableDirectionKeys = new Set(availablePropDirections.map((item) => item.key));

        setSelectedBetTypes((prev) => prev.filter((item) => availableBetTypeKeys.has(item)));
        setPropMarketTypes((prev) => prev.filter((item) => availableMarketKeys.has(item)));
        setPropDirections((prev) => prev.filter((item) => availableDirectionKeys.has(item)));
    }, [source, availableBetTypes, availableScoutingPropMarketTypes, availablePropDirections]);

    /**
     * Prune stat-analysis selections when options change.
     */
    useEffect(() => {
        if (source !== 'statAnalysis') {
            return;
        }

        const availableSnipedTypeKeys = new Set(availableSnipedTypes.map((item) => item.key));
        const availableSnipedPropTypeKeys = new Set(availableSnipedPropTypes.map((item) => item.key));

        setSnipedType((prev) => (prev && availableSnipedTypeKeys.has(prev) ? prev : null));
        setSnipedFilters((prev) => ({
            ...prev,
            betType: 'player_prop',
            propTypes: prev.propTypes.filter((item) => availableSnipedPropTypeKeys.has(item))
        }));
    }, [source, availableSnipedTypes, availableSnipedPropTypes]);
    
    /**
     * Toggles the expanded state of a section
     * @param {string} sectionKey - The key of the section to toggle
     */
    const toggleSection = (sectionKey) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };
    
    
    /**
     * Validates the configuration before generating parlays
     * @returns {Array} Array of error messages (empty if valid)
     */
    const validateConfiguration = () => {
        const errors = [];
        
        // Validate num_events (must be between 2 and 20)
        if (typeof numEvents !== 'number' || numEvents < 2 || numEvents > 20) {
            errors.push('Number of events must be between 2 and 20');
        }
        
        // Validate max_parlays (must be between 1 and 10)
        if (typeof maxParlays !== 'number' || maxParlays < 1 || maxParlays > 10) {
            errors.push('Maximum parlays must be between 1 and 10');
        }
        
        // Source-specific validation
        if (source === 'statAnalysis') {
            if (snipedType && !['overs', 'unders'].includes(snipedType)) {
                errors.push('Sniped type must be "overs" or "unders"');
            }
            
            // Validate snipedFilters if provided
            if (snipedFilters.minValueScore !== null && snipedFilters.minValueScore !== undefined) {
                if (typeof snipedFilters.minValueScore !== 'number' || snipedFilters.minValueScore < 0 || snipedFilters.minValueScore > 1) {
                    errors.push('Minimum value score must be between 0.0 and 1.0');
                }
            }
            
            if (snipedFilters.limit) {
                if (typeof snipedFilters.limit !== 'number' || snipedFilters.limit < 1 || snipedFilters.limit > 1000) {
                    errors.push('Limit must be between 1 and 1000');
                }
            }
        }
        
        return errors;
    };
    
    /**
     * Handles generate button click
     * Validates configuration and sends to API
     */
    const handleGenerate = async () => {
        if (!canUseParlayGenerator) {
            if (access.tier !== 'anonymous') {
                setShowUpgradeModal(true);
                return;
            }

            eventBus.emit(EVENT_NOTIFICATION_PUSH,
                new NotificationModel('Upgrade to Pro to unlock the parlay generator.', NOTIFICATIONS_ERROR)
            );
            return;
        }

        // Validate configuration
        const validationErrors = validateConfiguration();
        if (validationErrors.length > 0) {
            eventBus.emit(EVENT_NOTIFICATION_PUSH, 
                new NotificationModel(validationErrors.join(' '), NOTIFICATIONS_ERROR)
            );
            return;
        }
        
        setIsLoading(true);
        try {
            // Send configuration to API with source-specific parameters
            const response = await generateParlaysAPI(
                selectedSports, 
                numEvents, 
                maxParlays, 
                selectedBetTypes, 
                propMarketTypes,
                propDirections,
                source,
                snipedType,
                snipedFilters
            );
            const { data } = response;
            
            if (data.success && data.results && data.results.parlays) {
                AnalyticsService.getInstance().track(EVENT_PARLAY_GENERATED, {
                    success: true,
                    numEvents: numEvents,
                    maxParlays: maxParlays,
                    source: source,
                    sports: selectedSports,
                    betTypes: selectedBetTypes
                });
                // Map API response to component format
                // API returns snake_case: num_legs, date_time
                const mappedParlays = data.results.parlays.map((parlay, parlayIndex) => ({
                    id: parlay.id || parlay.parlayId || parlay.parlay_id || `generated-${Date.now()}-${parlayIndex}`,
                    bets: (parlay.bets || []).map(bet => ({
                        ...bet,
                        rawLeg: bet,
                        id: bet.id || bet.sourceBetId || bet.source_bet_id || null,
                        betType: bet.betType || bet.bet_type || null,
                        label: bet.label || bet.bet || bet.recommendedBet || bet.recommended_bet || '',
                        recommendedBet: bet.recommendedBet || bet.recommended_bet || bet.bet || bet.label || '',
                        eventId: bet.eventId || bet.event_id || bet.oddsApiEventId || bet.odds_api_event_id || bet.scoutingReportId || bet.scouting_report_id || null,
                        oddsApiEventId: bet.oddsApiEventId || bet.odds_api_event_id || bet.eventId || bet.event_id || null,
                        type: bet.type,
                        bet: bet.bet,
                        event: bet.event,
                        eventName: bet.eventName || bet.event_name || bet.event || '',
                        sport: bet.sport,
                        date: bet.date_time || bet.dateTime, // Handle both snake_case and camelCase
                        explanation: bet.explanation || [],
                        player: bet.player || null,
                        marketType: bet.marketType || bet.market_type || null,
                        side: bet.side || bet.team || null,
                        direction: bet.direction || null,
                        line: bet.line ?? null,
                        // Prop/team confidence from scouting-style legs when the API includes it
                        confidence: bet.confidence || bet.prop_bet_confidence || null
                    })),
                    num_legs: parlay.num_legs || parlay.numLegs || (parlay.bets ? parlay.bets.length : 0)
                }));

                if (mappedParlays.length === 0) {
                    eventBus.emit(
                        EVENT_NOTIFICATION_PUSH,
                        new NotificationModel(
                            'We were unable to generate parlay combinations for your current settings. Try again with different settings.',
                            NOTIFICATIONS_ERROR
                        )
                    );
                }

                setParlays(mappedParlays);
            } else {
                AnalyticsService.getInstance().track(EVENT_PARLAY_GENERATED, {
                    success: false,
                    numEvents: numEvents,
                    maxParlays: maxParlays,
                    source: source
                });
                console.error('Error generating parlays:', data.error);
                eventBus.emit(EVENT_NOTIFICATION_PUSH, 
                    new NotificationModel(data.error || 'Failed to generate parlays. Please try again.', NOTIFICATIONS_ERROR)
                );
                setParlays([]);
            }
        } catch (error) {
            AnalyticsService.getInstance().track(EVENT_PARLAY_GENERATED, {
                success: false,
                numEvents: numEvents,
                maxParlays: maxParlays,
                source: source
            });
            console.error('Error generating parlays:', error);
            eventBus.emit(EVENT_NOTIFICATION_PUSH, 
                new NotificationModel('An error occurred while generating parlays. Please try again.', NOTIFICATIONS_ERROR)
            );
            setParlays([]);
        } finally {
            setIsLoading(false);
        }
    };
    
    // No need to fetch events on mount - API will generate parlays when button is clicked
    
    /**
     * Formats date for display
     * Handles ISO 8601 date strings from the API
     */
    const formatDate = (date) => {
        if (!date) return null;
        try {
            const d = new Date(date);
            if (isNaN(d.getTime())) return null;
            return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (error) {
            console.error('Error formatting date:', error);
            return null;
        }
    };
    
    return (
        <PageView
            containerClassName={componentStyles.parlayGeneratorContainer}
            contentClassName={componentStyles.parlayGeneratorContent}
        >
            {!canUseParlayGenerator ? (
                <>
                    {access.tier === 'anonymous' && (
                        <AccessLockedPanel
                            title="Login Required"
                            message="Log in to view account access and upgrade options."
                            actionLabel="Log In"
                            actionView="login"
                        />
                    )}
                    <RegistrationModal
                        isOpen={showUpgradeModal}
                        onClose={() => setShowUpgradeModal(false)}
                        title="Upgrade to Pro"
                        suggestionProps={{
                            title: 'Unlock Parlay Generator',
                            subtitle: 'Upgrade to Pro to generate parlays with sniped odds and advanced filters.',
                            primaryLabel: 'View Pro Options',
                            primaryView: 'profile',
                            secondaryLabel: '',
                            secondaryView: '',
                            ctaContext: 'parlay_upgrade'
                        }}
                    />
                </>
            ) : (
                <>
            
            {/* Statistics Section - Hidden per user request */}
            {/* <div className={componentStyles.statsSection}>
                <div className={componentStyles.statCard}>
                    <div className={componentStyles.statLabel}>Total Events</div>
                    <div className={componentStyles.statValue}>{stats.totalEvents}</div>
                </div>
                <div className={componentStyles.statCard}>
                    <div className={componentStyles.statLabel}>Total Bets</div>
                    <div className={componentStyles.statValue}>{stats.totalBets}</div>
                </div>
                <div className={componentStyles.statCard}>
                    <div className={componentStyles.statLabel}>Generated Parlays</div>
                    <div className={componentStyles.statValue}>{parlays.length}</div>
                </div>
            </div> */}
            
            {/* Settings Button, Generate Button, and Parlay Count Row */}
            <div className={`${globalStyles.actionButtonsRow} ${componentStyles.parlayButtonsRow}`}>
                <button 
                    onClick={() => {
                        AnalyticsService.getInstance().track(EVENT_PARLAY_SETTINGS_OPENED, { source });
                        setIsSettingsModalOpen(true);
                    }}
                    className={`${globalStyles.iconButton} ${componentStyles.settingsButton}`}
                    title="Parlay Generator Settings"
                >
                    <FaCog />
                    <span>Settings</span>
                </button>
                
                <button 
                    onClick={handleGenerate}
                    className={`${globalStyles.primaryButton} ${componentStyles.generateButton}`}
                    disabled={isLoading}
                >
                    Generate
                </button>
                
                {/*<div 
                    className={componentStyles.parlayCountBadge}
                >
                    Parlay Count: ({parlays.length})
                </div>*/}
            </div>
            
            {/* Settings Modal */}
            <ParlaySettingsModal 
                isOpen={isSettingsModalOpen} 
                onClose={() => setIsSettingsModalOpen(false)}
            >
                <div className={componentStyles.modalConfigContent}>
                    {/* Data Source Section - Collapsible */}
                    <div className={componentStyles.collapsibleSection}>
                        <button
                            type="button"
                            className={componentStyles.collapsibleHeader}
                            onClick={() => toggleSection('dataSource')}
                        >
                            <div className={componentStyles.collapsibleHeaderContent}>
                                {expandedSections.dataSource ? <FaChevronDown /> : <FaChevronRight />}
                                <span className={componentStyles.collapsibleTitle}>Data Source</span>
                            </div>
                            <span className={componentStyles.collapsibleValue}>
                                Sniped Odds
                            </span>
                        </button>
                        {expandedSections.dataSource && (
                            <div className={componentStyles.collapsibleContent}>
                                <div className={componentStyles.configRow}>
                                    <div className={componentStyles.configLabel}>
                                        Data Source
                                        <div className={componentStyles.toggleContainer}>
                                            <button
                                                type="button"
                                                className={`${componentStyles.viewModeToggle} ${source === 'statAnalysis' ? componentStyles.viewModeToggleActive : ''}`}
                                                onClick={() => setSource('statAnalysis')}
                                            >
                                                Sniped Odds
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Number of Events Section - Collapsible */}
                    <div className={componentStyles.collapsibleSection}>
                        <button
                            type="button"
                            className={componentStyles.collapsibleHeader}
                            onClick={() => toggleSection('numEvents')}
                        >
                            <div className={componentStyles.collapsibleHeaderContent}>
                                {expandedSections.numEvents ? <FaChevronDown /> : <FaChevronRight />}
                                <span className={componentStyles.collapsibleTitle}>Number of Events per Parlay</span>
                            </div>
                            <span className={componentStyles.collapsibleValue}>{numEvents}</span>
                        </button>
                        {expandedSections.numEvents && (
                            <div className={componentStyles.collapsibleContent}>
                                <div className={componentStyles.configRow}>
                                    <label className={componentStyles.configLabel}>
                                        Number of Events per Parlay: {numEvents}
                                        <div className={componentStyles.sliderContainer}>
                                            <input
                                                type="range"
                                                min="2"
                                                max="20"
                                                value={numEvents}
                                                onChange={(e) => setNumEvents(Number(e.target.value))}
                                                className={componentStyles.slider}
                                            />
                                            <div className={componentStyles.sliderLabels}>
                                                <span>2</span>
                                                <span>20</span>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Maximum Parlays Section - Collapsible */}
                    <div className={componentStyles.collapsibleSection}>
                        <button
                            type="button"
                            className={componentStyles.collapsibleHeader}
                            onClick={() => toggleSection('maxParlays')}
                        >
                            <div className={componentStyles.collapsibleHeaderContent}>
                                {expandedSections.maxParlays ? <FaChevronDown /> : <FaChevronRight />}
                                <span className={componentStyles.collapsibleTitle}>Maximum Parlays</span>
                            </div>
                            <span className={componentStyles.collapsibleValue}>{maxParlays}</span>
                        </button>
                        {expandedSections.maxParlays && (
                            <div className={componentStyles.collapsibleContent}>
                                <div className={componentStyles.configRow}>
                                    <label className={componentStyles.configLabel}>
                                        Maximum Parlays: {maxParlays}
                                        <div className={componentStyles.sliderContainer}>
                                            <input
                                                type="range"
                                                min="1"
                                                max="10"
                                                value={maxParlays}
                                                onChange={(e) => setMaxParlays(Number(e.target.value))}
                                                className={componentStyles.slider}
                                            />
                                            <div className={componentStyles.sliderLabels}>
                                                <span>1</span>
                                                <span>10</span>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Sport Filter Section - Collapsible */}
                    <div className={componentStyles.collapsibleSection}>
                        <button
                            type="button"
                            className={componentStyles.collapsibleHeader}
                            onClick={() => toggleSection('sportFilter')}
                        >
                            <div className={componentStyles.collapsibleHeaderContent}>
                                {expandedSections.sportFilter ? <FaChevronDown /> : <FaChevronRight />}
                                <span className={componentStyles.collapsibleTitle}>Sport Filter</span>
                            </div>
                            <span className={componentStyles.collapsibleValue}>
                                {selectedSports.length > 0 ? `${selectedSports.length} selected` : 'All sports'}
                            </span>
                        </button>
                        {expandedSections.sportFilter && (
                            <div className={componentStyles.collapsibleContent}>
                                <div className={componentStyles.configRow}>
                                    <div className={componentStyles.sportFilterContainer}>
                                        <div className={componentStyles.configLabel}>Sport Filter</div>
                                        <div className={componentStyles.sportPillsContainer}>
                                            {userSports.map((sport) => {
                                                const sportLower = sport.toLowerCase();
                                                const isSelected = selectedSports.includes(sportLower);
                                                
                                                return (
                                                    <button
                                                        key={sport}
                                                        type="button"
                                                        className={`${componentStyles.sportPill} ${isSelected ? componentStyles.sportPillActive : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            
                                                            // Use functional update to ensure we have the latest state
                                                            setSelectedSports(prev => {
                                                                const currentSport = sport.toLowerCase();
                                                                const isCurrentlySelected = prev.includes(currentSport);
                                                                
                                                                let newState;
                                                                if (isCurrentlySelected) {
                                                                    // Remove from array
                                                                    newState = prev.filter(s => s !== currentSport);
                                                                } else {
                                                                    // Add to array
                                                                    newState = [...prev, currentSport];
                                                                }
                                                                
                                                                return newState;
                                                            });
                                                        }}
                                                    >
                                                        {sport.toUpperCase()}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {parlayOptionsError && (
                        <div className={componentStyles.helpText}>
                            {parlayOptionsError}
                        </div>
                    )}
                    
                    {/* Sniped Type Section - Only show for stat analysis - Collapsible */}
                    {source === 'statAnalysis' && (
                        <div className={componentStyles.collapsibleSection}>
                            <button
                                type="button"
                                className={componentStyles.collapsibleHeader}
                                onClick={() => toggleSection('snipedType')}
                            >
                                <div className={componentStyles.collapsibleHeaderContent}>
                                    {expandedSections.snipedType ? <FaChevronDown /> : <FaChevronRight />}
                                    <span className={componentStyles.collapsibleTitle}>Sniped Type (Optional)</span>
                                </div>
                                <span className={componentStyles.collapsibleValue}>
                                    {snipedType ? snipedType.charAt(0).toUpperCase() + snipedType.slice(1) : 'Not selected'}
                                </span>
                            </button>
                            {expandedSections.snipedType && (
                                <div className={componentStyles.collapsibleContent}>
                                    <div className={componentStyles.configRow}>
                                        <div className={componentStyles.sportFilterContainer}>
                                        <div className={componentStyles.configLabel}>Sniped Type (Optional)</div>
                                        <div className={componentStyles.sportPillsContainer}>
                                                {availableSnipedTypes.map((typeOption) => {
                                                    const isSelected = snipedType === typeOption.key;
                                                    
                                                    return (
                                                        <button
                                                            key={typeOption.key}
                                                            type="button"
                                                            className={`${componentStyles.sportPill} ${isSelected ? componentStyles.sportPillActive : ''}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                setSnipedType((current) => current === typeOption.key ? null : typeOption.key);
                                                            }}
                                                        >
                                                            {typeOption.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <div className={componentStyles.helpText}>
                                                Select the type of sniped odds to use for parlay generation
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Bet Types Section - Only show for scouting reports - Collapsible */}
                    {source === 'scoutingReports' && (
                        <div className={componentStyles.collapsibleSection}>
                        <button
                            type="button"
                            className={componentStyles.collapsibleHeader}
                            onClick={() => toggleSection('betTypes')}
                        >
                            <div className={componentStyles.collapsibleHeaderContent}>
                                {expandedSections.betTypes ? <FaChevronDown /> : <FaChevronRight />}
                                <span className={componentStyles.collapsibleTitle}>Bet Types to Include</span>
                            </div>
                            <span className={componentStyles.collapsibleValue}>
                                {selectedBetTypes.length > 0 ? `${selectedBetTypes.length} selected` : 'All bet types'}
                            </span>
                        </button>
                        {expandedSections.betTypes && (
                            <div className={componentStyles.collapsibleContent}>
                                <div className={componentStyles.configRow}>
                                    <div className={componentStyles.sportFilterContainer}>
                                        <div className={componentStyles.configLabel}>Bet Types to Include</div>
                                        <div className={componentStyles.sportPillsContainer}>
                                            {availableBetTypes.map((betType) => {
                                                const isSelected = selectedBetTypes.includes(betType.key);
                                                
                                                return (
                                                    <button
                                                        key={betType.key}
                                                        type="button"
                                                        className={`${componentStyles.sportPill} ${isSelected ? componentStyles.sportPillActive : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            
                                                            // Use functional update to ensure we have the latest state
                                                            setSelectedBetTypes(prev => {
                                                                const isCurrentlySelected = prev.includes(betType.key);
                                                                
                                                                let newState;
                                                                if (isCurrentlySelected) {
                                                                    // Remove from array
                                                                    newState = prev.filter(t => t !== betType.key);
                                                                } else {
                                                                    // Add to array
                                                                    newState = [...prev, betType.key];
                                                                }
                                                                
                                                                return newState;
                                                            });
                                                        }}
                                                    >
                                                        {betType.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <div className={componentStyles.helpText}>
                                            Leave all unselected to include all bet types
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    )}
                    
                    {/* Prop Market Types Filter - Only show for scouting reports when Prop is selected - Collapsible */}
                    {source === 'scoutingReports' && selectedBetTypes.includes('Prop') && (
                        <div className={componentStyles.collapsibleSection}>
                            <button
                                type="button"
                                className={componentStyles.collapsibleHeader}
                                onClick={() => toggleSection('propMarketTypes')}
                            >
                                <div className={componentStyles.collapsibleHeaderContent}>
                                    {expandedSections.propMarketTypes ? <FaChevronDown /> : <FaChevronRight />}
                                    <span className={componentStyles.collapsibleTitle}>Prop Market Types</span>
                                </div>
                                <span className={componentStyles.collapsibleValue}>
                                    {propMarketTypes.length > 0 ? `${propMarketTypes.length} selected` : 'All market types'}
                                </span>
                            </button>
                            {expandedSections.propMarketTypes && (
                                <div className={componentStyles.collapsibleContent}>
                                    <div className={componentStyles.configRow}>
                                        <div className={componentStyles.sportFilterContainer}>
                                            <div className={componentStyles.configLabel}>Prop Market Types</div>
                                            <div className={componentStyles.sportPillsContainer}>
                                                {availableScoutingPropMarketTypes.map((marketType) => {
                                                    const isSelected = propMarketTypes.includes(marketType.key);
                                                    
                                                    return (
                                                        <button
                                                            key={marketType.key}
                                                            type="button"
                                                            className={`${componentStyles.sportPill} ${isSelected ? componentStyles.sportPillActive : ''}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                
                                                                // Use functional update to ensure we have the latest state
                                                                setPropMarketTypes(prev => {
                                                                    const isCurrentlySelected = prev.includes(marketType.key);
                                                                    
                                                                    let newState;
                                                                    if (isCurrentlySelected) {
                                                                        // Remove from array
                                                                        newState = prev.filter(t => t !== marketType.key);
                                                                    } else {
                                                                        // Add to array
                                                                        newState = [...prev, marketType.key];
                                                                    }
                                                                    
                                                                    return newState;
                                                                });
                                                            }}
                                                        >
                                                            {marketType.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <div className={componentStyles.helpText}>
                                                Leave all unselected to include all prop market types
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Prop Directions Filter - Only show for scouting reports when Prop is selected - Collapsible */}
                    {source === 'scoutingReports' && selectedBetTypes.includes('Prop') && (
                        <div className={componentStyles.collapsibleSection}>
                            <button
                                type="button"
                                className={componentStyles.collapsibleHeader}
                                onClick={() => toggleSection('propDirections')}
                            >
                                <div className={componentStyles.collapsibleHeaderContent}>
                                    {expandedSections.propDirections ? <FaChevronDown /> : <FaChevronRight />}
                                    <span className={componentStyles.collapsibleTitle}>Prop Directions</span>
                                </div>
                                <span className={componentStyles.collapsibleValue}>
                                    {propDirections.length > 0 ? `${propDirections.length} selected` : 'All directions'}
                                </span>
                            </button>
                            {expandedSections.propDirections && (
                                <div className={componentStyles.collapsibleContent}>
                                    <div className={componentStyles.configRow}>
                                        <div className={componentStyles.sportFilterContainer}>
                                            <div className={componentStyles.configLabel}>Prop Directions</div>
                                            <div className={componentStyles.sportPillsContainer}>
                                                {availablePropDirections.map((direction) => {
                                                    const isSelected = propDirections.includes(direction.key);

                                                    return (
                                                        <button
                                                            key={direction.key}
                                                            type="button"
                                                            className={`${componentStyles.sportPill} ${isSelected ? componentStyles.sportPillActive : ''}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                setPropDirections((prev) => {
                                                                    const isCurrentlySelected = prev.includes(direction.key);
                                                                    return isCurrentlySelected
                                                                        ? prev.filter((item) => item !== direction.key)
                                                                        : [...prev, direction.key];
                                                                });
                                                            }}
                                                        >
                                                            {direction.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <div className={componentStyles.helpText}>
                                                Leave all unselected to include all prop directions
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Sniped Filters Section - Only show for stat analysis - Collapsible (Progressive Disclosure) */}
                    {source === 'statAnalysis' && (
                        <div className={componentStyles.collapsibleSection}>
                            <button
                                type="button"
                                className={componentStyles.collapsibleHeader}
                                onClick={() => toggleSection('snipedFilters')}
                            >
                                <div className={componentStyles.collapsibleHeaderContent}>
                                    {expandedSections.snipedFilters ? <FaChevronDown /> : <FaChevronRight />}
                                    <span className={componentStyles.collapsibleTitle}>Sniped Filters (Optional)</span>
                                </div>
                                <span className={componentStyles.collapsibleValue}>
                                    {Object.values(snipedFilters).some(v => v !== null && v !== undefined && (Array.isArray(v) ? v.length > 0 : v !== false && v !== '')) ? 'Filters active' : 'No filters'}
                                </span>
                            </button>
                            {expandedSections.snipedFilters && (
                                <div className={componentStyles.collapsibleContent}>
                                    {/* Parlay generation is currently limited to player props. */}
                                    {snipedFilters.betType === 'player_prop' && (
                                        <div className={componentStyles.configRow}>
                                            <div className={componentStyles.sportFilterContainer}>
                                            <div className={componentStyles.configLabel}>Prop Types</div>
                                                {parlayOptionsLoading ? (
                                                    <div className={componentStyles.helpText}>Loading available props...</div>
                                                ) : availableSnipedPropTypes.length > 0 ? (
                                                    <div className={componentStyles.sportPillsContainer}>
                                                        {availableSnipedPropTypes.map((prop) => {
                                                            const isSelected = snipedFilters.propTypes.includes(prop.key);
                                                            
                                                            return (
                                                                <button
                                                                    key={prop.key}
                                                                    type="button"
                                                                    className={`${componentStyles.sportPill} ${isSelected ? componentStyles.sportPillActive : ''}`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        e.preventDefault();
                                                                        setSnipedFilters(prev => ({
                                                                            ...prev,
                                                                            propTypes: prev.propTypes.includes(prop.key)
                                                                                ? prev.propTypes.filter(p => p !== prop.key)
                                                                                : [...prev.propTypes, prop.key]
                                                                        }));
                                                                    }}
                                                                >
                                                                    {prop.label}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <div className={componentStyles.helpText}>
                                                        Select a sport to see available prop types
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* DFS Filter */}
                                    <div className={componentStyles.configRow}>
                                        <div className={componentStyles.sportFilterContainer}>
                                            <div className={componentStyles.configLabel}>DFS Filter</div>
                                            <div className={componentStyles.sportPillsContainer}>
                                                {[
                                                    { value: null, label: 'All' },
                                                    { value: true, label: 'DFS Only' },
                                                    { value: false, label: 'Non-DFS Only' }
                                                ].map((option) => {
                                                    const isSelected = snipedFilters.isDfs === option.value;
                                                    
                                                    return (
                                                        <button
                                                            key={option.label}
                                                            type="button"
                                                            className={`${componentStyles.sportPill} ${isSelected ? componentStyles.sportPillActive : ''}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                setSnipedFilters(prev => ({
                                                                    ...prev,
                                                                    isDfs: option.value
                                                                }));
                                                            }}
                                                        >
                                                            {option.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Recommendation Filter */}
                                    <div className={componentStyles.configRow}>
                                        <div className={componentStyles.sportFilterContainer}>
                                            <div className={componentStyles.configLabel}>Recommendation Tier</div>
                                            <div className={componentStyles.sportPillsContainer}>
                                                {['Strong', 'Good', 'Decent', 'Consider'].map((rec) => {
                                                    const isSelected = snipedFilters.recommendation === rec;
                                                    
                                                    return (
                                                        <button
                                                            key={rec}
                                                            type="button"
                                                            className={`${componentStyles.sportPill} ${isSelected ? componentStyles.sportPillActive : ''}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                setSnipedFilters(prev => ({
                                                                    ...prev,
                                                                    recommendation: prev.recommendation === rec ? null : rec
                                                                }));
                                                            }}
                                                        >
                                                            {rec}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Min Value Score */}
                                    <div className={componentStyles.configRow}>
                                        <div className={componentStyles.configLabel}>
                                            Minimum Value Score (0.0 - 1.0)
                                            <input
                                                type="number"
                                                min="0"
                                                max="1"
                                                step="0.01"
                                                value={snipedFilters.minValueScore !== null && snipedFilters.minValueScore !== undefined ? snipedFilters.minValueScore : ''}
                                                onChange={(e) => {
                                                    const value = e.target.value === '' ? null : parseFloat(e.target.value);
                                                    setSnipedFilters(prev => ({
                                                        ...prev,
                                                        minValueScore: value
                                                    }));
                                                }}
                                                placeholder="No minimum"
                                                className={componentStyles.numberInput}
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Limit */}
                                    <div className={componentStyles.configRow}>
                                        <div className={componentStyles.configLabel}>
                                            Limit (1 - 1000)
                                            <input
                                                type="number"
                                                min="1"
                                                max="1000"
                                                value={snipedFilters.limit || 100}
                                                onChange={(e) => {
                                                    const value = parseInt(e.target.value) || 100;
                                                    setSnipedFilters(prev => ({
                                                        ...prev,
                                                        limit: value
                                                    }));
                                                }}
                                                className={componentStyles.numberInput}
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Include All Opportunities */}
                                    <div className={componentStyles.configRow}>
                                        <div className={componentStyles.configLabel}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={snipedFilters.includeAllOpportunities || false}
                                                    onChange={(e) => {
                                                        setSnipedFilters(prev => ({
                                                            ...prev,
                                                            includeAllOpportunities: e.target.checked
                                                        }));
                                                    }}
                                                />
                                                Include All Opportunities
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                </div>
            </ParlaySettingsModal>
            
            {/* Loading State */}
            {isLoading && (
                <div className={componentStyles.loadingSection}>
                    <SmallLoadingAlt />
                    <p>Generating parlays...</p>
                </div>
            )}
            
            {/* Parlays Display Section */}
            {!isLoading && (
                <div className={componentStyles.parlaysSection}>
                    {parlays.length === 0 ? (
                        null
                    ) : (
                        <ParlayView 
                            parlays={parlays}
                            viewMode={viewMode}
                            formatDate={formatDate}
                        />
                    )}
                </div>
            )}
                </>
            )}
        </PageView>
    );
};

export default ParlayGenerator;
